import { useToast } from '@chakra-ui/react'
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import Icon from '../../../../../../components/Icon'
import Switch from '../../../../../../components/Switch'
import Tooltip from '../../../../../../components/Tooltip'
import { useUpdateWalletMutation } from '../../../../../../redux/services/coreApi'
import type {
  WalletDto,
  WalletHostingType,
} from '../../../../../../types/coreApi-types'

interface EditWalletModalProps {
  isOpen: boolean
  onClose: () => void
  wallet: WalletDto | null
}

const EditWalletModal: React.FC<EditWalletModalProps> = ({
  isOpen,
  onClose,
  wallet,
}) => {
  const toast = useToast()

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [destinationTag, setDestinationTag] = useState<string>('')
  const [active, setActive] = useState(false)
  const [hostingType, setHostingType] = useState<WalletHostingType>('Unknown')
  const [hostedVaspId, setHostedVaspId] = useState<string>('')

  const [updateWallet, { isLoading }] = useUpdateWalletMutation()

  useEffect(() => {
    if (wallet) {
      setName(wallet.name || '')
      setAddress(wallet.address)
      setDestinationTag(wallet.destinationTag || '')
      // Because we get wallet from table which maps boolean to string "Active" or "Inactive", we also expect that here
      // @ts-expect-error
      setActive(wallet.active === 'Active')
      setHostingType(wallet.hostingType || 'Unknown')
      setHostedVaspId(wallet.hostedVaspId || '')
    }
  }, [wallet])

  const handleSubmit = async () => {
    if (!wallet) return

    try {
      await updateWallet({
        id: wallet.id,
        name: name || null,
        address,
        destinationTag,
        active,
        hostingType,
        hostedVaspId: hostedVaspId || null,
      }).unwrap()

      toast({
        title: 'Success',
        description: 'Wallet updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      onClose()
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      isLoadingText="Updating wallet..."
      title="Edit System Wallet"
    >
      <div className="p-5 pt-0 space-y-5">
        {wallet && (
          <>
            <div className="flex items-center gap-2 mb-5">
              <Icon src={wallet.network} size="md" />
              <div>
                <p className="text-default-base font-medium">
                  {wallet.network.name}
                </p>
                {wallet.network.nativeCurrency && (
                  <p className="text-sm text-description">
                    {wallet.network.nativeCurrency.isoCode}
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="mb-1 text-description">Wallet Name (optional)</p>
              <input
                type="text"
                className="input-card w-full"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter a name for this wallet"
              />
            </div>

            <div>
              <p className="mb-1 text-description">Wallet Address</p>
              <input
                type="text"
                className="input-card w-full"
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <p className="text-description">Destination tag / memo</p>
                <Tooltip label="Destination tags and memos are optional identifiers used by certain networks (like XRP, XLM, etc.) to route transactions to the correct recipient. Only fill this field if the network you're using requires it or if you need to specify a particular destination.">
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  />
                </Tooltip>
              </div>
              <input
                type="text"
                className="input-card w-full"
                value={destinationTag}
                onChange={e => setDestinationTag(e.target.value)}
                placeholder="Optional - only for networks that require it"
              />
            </div>

            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setActive(!active)}
            >
              <p className="text-description">Active Status</p>
              <Switch isOn={active} />
            </div>

            <div>
              <p className="mb-2 text-description">Wallet Type</p>
              <div className="space-y-2">
                {(
                  ['SelfHosted', 'VaspHosted', 'Unknown'] as WalletHostingType[]
                ).map(type => (
                  <label
                    key={type}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="hostingType"
                      value={type}
                      checked={hostingType === type}
                      onChange={e =>
                        setHostingType(e.target.value as WalletHostingType)
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm">
                      {type === 'SelfHosted'
                        ? 'Self Hosted'
                        : type === 'VaspHosted'
                          ? 'VASP Hosted'
                          : 'Unknown'}
                    </span>
                    {type === 'Unknown' && (
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="w-3 h-3 text-red-500"
                      />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {hostingType === 'VaspHosted' && (
              <div>
                <p className="mb-1 text-description">VASP ID</p>
                <input
                  type="text"
                  className="input-card w-full"
                  value={hostedVaspId}
                  onChange={e => setHostedVaspId(e.target.value)}
                  placeholder="Enter VASP ID"
                />
              </div>
            )}

            <button
              onClick={handleSubmit}
              className="button w-full mt-6"
              disabled={!address.trim()}
            >
              Save Changes
            </button>
          </>
        )}
      </div>
    </DefaultModal>
  )
}

export default EditWalletModal
